import { supabase } from '../../../config/supabase';
import {
  STRUCTURED_ANAMNESIS_KIND,
  formatAnamnesisSections,
  hasAnamnesisContent,
  isStructuredAnamnesisRecord,
  type AnamnesisSections,
} from '../../../utils/clinicalDocuments';
import { buildMedicalRecordAuthorSnapshot } from '../../../utils/medicalRecordAuthor';
import {
  getMedicalRecordAttachmentsObject,
  isMedicalRecordInvalidated,
} from '../../../utils/medicalRecordStatus';
import {
  buildInitialAssessmentAnamnesisOption,
  type InitialAssessmentAnamnesisOption,
  type InitialAssessmentAnamnesisSourceRecord,
} from './initialAssessmentAnamnesis';

export interface CreateInitialAssessmentAnamnesisInput {
  clientId: string;
  patientId: string;
  userId: string;
  userName?: string | null;
  userRole?: string | null;
  recordDate: string;
  sections: AnamnesisSections;
}

const getDocumentVersion = (record: InitialAssessmentAnamnesisSourceRecord): number => {
  const attachments = getMedicalRecordAttachmentsObject(record.recordAttachments);
  const version = Number(attachments.clinicalDocumentVersion);
  return Number.isInteger(version) && version > 0 ? version : 1;
};

const getLatestStructuredAnamnesis = (
  records: InitialAssessmentAnamnesisSourceRecord[],
): InitialAssessmentAnamnesisSourceRecord | null =>
  records
    .filter(
      (record) =>
        isStructuredAnamnesisRecord(record.recordAttachments) &&
        !isMedicalRecordInvalidated(record.recordType, record.recordAttachments),
    )
    .sort((left, right) => {
      const versionDelta = getDocumentVersion(right) - getDocumentVersion(left);
      if (versionDelta !== 0) return versionDelta;
      return new Date(right.recordDate).getTime() - new Date(left.recordDate).getTime();
    })[0] ?? null;

export class InitialAssessmentAnamnesisService {
  static async create(
    input: CreateInitialAssessmentAnamnesisInput,
  ): Promise<InitialAssessmentAnamnesisOption> {
    if (!input.clientId || !input.patientId || !input.userId) {
      throw new Error('Não foi possível identificar paciente, clínica ou profissional.');
    }
    if (!input.recordDate || Number.isNaN(new Date(input.recordDate).getTime())) {
      throw new Error('Informe uma data válida para a anamnese.');
    }
    if (!hasAnamnesisContent(input.sections)) {
      throw new Error('Preencha pelo menos uma seção da anamnese.');
    }

    const { data: existingData, error: existingError } = await supabase
      .from('medical_records')
      .select('recordId, recordDate, recordTitle, recordDescription, recordType, recordAttachments')
      .eq('clientId', input.clientId)
      .eq('patientId', input.patientId)
      .order('recordDate', { ascending: false });

    if (existingError) throw existingError;

    const latest = getLatestStructuredAnamnesis(
      (existingData || []) as InitialAssessmentAnamnesisSourceRecord[],
    );
    const nextVersion = latest ? getDocumentVersion(latest) + 1 : 1;
    const title = nextVersion > 1 ? 'Atualização da anamnese' : 'Anamnese';
    const authorSnapshot = buildMedicalRecordAuthorSnapshot({
      userId: input.userId,
      name: input.userName || null,
      role: input.userRole || null,
    });

    const { data: insertedData, error: insertError } = await supabase
      .from('medical_records')
      .insert({
        clientId: input.clientId,
        patientId: input.patientId,
        doctorId: input.userId,
        recordType: 'progress',
        recordDate: new Date(input.recordDate).toISOString(),
        recordTitle: title,
        recordDescription: formatAnamnesisSections(input.sections),
        recordAttachments: {
          clinicalDocumentKind: STRUCTURED_ANAMNESIS_KIND,
          clinicalHistoryType: nextVersion > 1 ? 'anamnesis_update' : 'anamnesis',
          schemaVersion: 1,
          anamnesisSections: input.sections,
          clinicalDocumentVersion: nextVersion,
          previousClinicalDocumentId: latest?.recordId || null,
          authorSnapshot,
          createdFrom: 'initial_assessment',
        },
      })
      .select('recordId, recordDate, recordTitle, recordDescription, recordType, recordAttachments')
      .single();

    if (insertError) throw insertError;

    const option = buildInitialAssessmentAnamnesisOption(
      insertedData as InitialAssessmentAnamnesisSourceRecord,
    );
    if (!option) {
      throw new Error('A anamnese foi registrada, mas não pôde ser vinculada à avaliação.');
    }

    return option;
  }
}
